import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Menu,
  X,
  ClipboardList,
  Car,
  Users,
  HeartHandshake,
  Wrench,
  Search,
  Package,
  ReceiptText,
  CalendarClock,
  Globe2,
  Wifi,
  Zap,
  Save,
  RefreshCw,
  ImageOff,
  CheckCircle2,
  Smartphone,
  Monitor,
  Signal,
  Languages,
  ShieldCheck,
  FileText,
  Camera,
  Phone,
  Mail,
  ChevronDown,
  Quote,
  History,
} from "lucide-react";
import { LANGUAGES, useI18n } from "@/lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// ---------------------------------------------------------------------------
// Fender by SparesHub — Public Homepage
// Section order (per Phase-1 spec):
//   Header · Hero · 8 Languages · SparesHub Customer Benefit · Benefits ·
//   Features · SparesHub Spare Parts Enquiry · Workshop Workflow ·
//   Offline Reliability · Mobile · Testimonials · Final CTA · Footer
// Legal entity: Iradium Automobiles Private Limited (copyright only).
// Brand endorsement: "Fender by SparesHub" (do NOT swap with legal name).
// ---------------------------------------------------------------------------

const LEGAL_NAME = "Iradium Automobiles Private Limited";
const COPYRIGHT_YEAR = 2026;

const NAV = [
  { href: "#features", label: "Features" },
  { href: "#how-it-works", label: "How It Works" },
  { href: "#mobile-app", label: "Mobile App" },
  { href: "#languages", label: "Languages" },
  { href: "#contact", label: "Contact" },
];

// Reusable primary CTA — single homepage CTA per Phase-1 spec.
// Do NOT use "Start Free" / "Get Started" / "Book Demo" anywhere.
const SignUpCTA = ({ size = "md", className = "", testId = "cta-sign-up", label = "Sign Up" }) => (
  <Link
    to="/register"
    data-testid={testId}
    className={[
      "inline-flex items-center justify-center gap-2 rounded-lg font-semibold",
      "bg-[#e63946] text-white hover:bg-[#d02f3c] transition-colors",
      "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e63946]",
      size === "lg" ? "h-12 px-6 text-[15px]" : "h-11 px-5 text-sm",
      className,
    ].join(" ")}
  >
    {label}
    <ArrowRight size={16} strokeWidth={2.5} />
  </Link>
);

// Compact language picker (header uses this)
const LangPicker = ({ compact = false }) => {
  const { lang, setLang } = useI18n();
  const active = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="landing-lang-switcher"
          className={[
            "inline-flex items-center gap-1.5 rounded-md text-[13px] font-semibold",
            "text-[#1d3557] hover:text-[#e63946] transition-colors",
            "focus:outline-none",
            compact ? "px-2 py-2 w-full justify-between border border-[#e6edf1]" : "px-2.5 py-2",
          ].join(" ")}
        >
          <span className="inline-flex items-center gap-1.5">
            <Globe2 size={15} strokeWidth={2} />
            <span>{active.label}</span>
          </span>
          <ChevronDown size={13} strokeWidth={2.5} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[170px] rounded-lg border border-[#e6edf1] p-1 bg-white shadow-lg"
      >
        {LANGUAGES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => setLang(l.code)}
            data-testid={`landing-lang-${l.code}`}
            className={[
              "cursor-pointer rounded-md text-[14px] px-3 py-2",
              l.code === lang
                ? "text-[#e63946] font-semibold bg-[#f1faee]"
                : "text-[#1d3557] hover:bg-[#f1faee]",
            ].join(" ")}
          >
            {l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ---------------- Header ---------------------------------------------------
const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header
      className="w-full bg-white border-b border-[#e6edf1] sticky top-0 z-40"
      data-testid="landing-header"
    >
      <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="landing-logo">
          <img
            src="/fender-logo-lockup.png"
            alt="Fender by SparesHub"
            className="h-9 md:h-10 w-auto"
            width="720"
            height="208"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="text-[14px] text-[#1d3557] hover:text-[#e63946] transition-colors font-medium"
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <LangPicker />
          <div className="w-px h-5 bg-[#e6edf1] mx-1" aria-hidden="true" />
          <Link
            to="/login"
            className="text-[14px] font-semibold text-[#1d3557] hover:text-[#e63946] px-3"
            data-testid="nav-login"
          >
            Login
          </Link>
          <SignUpCTA testId="header-sign-up" />
        </div>

        <button
          className="lg:hidden text-[#1d3557] p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#e6edf1] bg-white">
          <div className="max-w-[1200px] mx-auto px-6 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="py-3 text-[15px] font-medium text-[#1d3557] border-b border-[#f1faee]"
              >
                {n.label}
              </a>
            ))}
            <Link
              to="/login"
              onClick={() => setOpen(false)}
              className="py-3 text-[15px] font-semibold text-[#1d3557]"
            >
              Login
            </Link>
            <div className="pt-2">
              <LangPicker compact />
            </div>
            <SignUpCTA className="w-full mt-2" testId="mobile-sign-up" />
          </div>
        </div>
      )}
    </header>
  );
};

// ---------------- Hero -----------------------------------------------------
const TrustIcon = ({ Icon, label }) => (
  <div className="flex items-center gap-2" data-testid={`trust-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="w-8 h-8 rounded-md bg-[#f1faee] grid place-items-center text-[#457b9d]">
      <Icon size={16} strokeWidth={2} />
    </div>
    <span className="text-[13px] text-[#1d3557] font-medium">{label}</span>
  </div>
);

const Hero = () => (
  <section className="bg-white" data-testid="hero-section">
    <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6">
        <h1 className="text-[34px] md:text-[44px] lg:text-[52px] font-bold text-[#1d3557] leading-[1.08] tracking-tight">
          World-class Garage Management Software for Workshops and Mechanics
        </h1>
        <p className="mt-5 text-[17px] md:text-[19px] text-[#1d3557] font-medium leading-snug max-w-[560px]">
          Manage job cards, appointments, estimates, spare parts, invoices, payments and vehicle delivery from one simple platform.
        </p>

        <div className="mt-7">
          <SignUpCTA size="lg" testId="hero-sign-up" />
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 max-w-[520px]">
          <TrustIcon Icon={Monitor} label="Web, Android & iOS" />
          <TrustIcon Icon={Signal} label="Works on weak internet" />
          <TrustIcon Icon={Languages} label="8 languages" />
          <TrustIcon Icon={ShieldCheck} label="Powered by SparesHub" />
        </div>
      </div>

      <div className="lg:col-span-6 relative">
        <HeroMockup />
      </div>
    </div>
  </section>
);

// Hero image: real product mockup (laptop + phone) provided by brand
const HeroMockup = () => (
  <div className="relative w-full" data-testid="hero-mockup">
    <picture>
      <source srcSet="/hero-image.webp" type="image/webp" />
      <img
        src="/hero-image.jpg"
        alt="Fender by SparesHub dashboard on laptop and mobile app"
        className="w-full h-auto"
        width="1400"
        height="875"
        loading="eager"
        fetchPriority="high"
      />
    </picture>
  </div>
);

// ---------------- Product Tour (auto-loop video) --------------------------
const ProductTour = () => (
  <section className="bg-[#f7f9fc]" data-testid="product-tour-section">
    <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-20">
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#457b9d]/10 text-[10px] uppercase tracking-[0.18em] font-bold text-[#457b9d] mb-4">
          Product Tour · Guided workshop walkthrough
        </div>
        <h2 className="text-[26px] md:text-[38px] font-bold text-[#1d3557] tracking-tight leading-[1.15]">
          See Fender in Action Before You Sign Up
        </h2>
        <p className="mt-4 text-[15px] text-[#457b9d] leading-relaxed">
          Watch a real user pick a language, open a job card, book an appointment, build a quotation, print a gate pass, and send a spare-parts enquiry to SparesHub — end-to-end in one clean flow.
        </p>
      </div>

      <div className="mt-10 relative mx-auto max-w-[1000px]" data-testid="product-tour-frame">
        <div className="relative rounded-[18px] p-3 md:p-4 bg-[#1d3557] shadow-2xl">
          {/* Fake browser chrome so the video reads as an app screen */}
          <div className="flex items-center gap-1.5 px-2 pb-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#e63946]/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#F79009]/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#12B76A]/70" />
            <span className="ml-3 text-[10.5px] font-mono text-white/50 truncate">app.fenderapp.com/dashboard</span>
          </div>
          <video
            src="/product-tour.mp4"
            poster="/product-tour-poster.jpg"
            className="w-full rounded-md block bg-black"
            autoPlay
            loop
            muted
            playsInline
            controls
            controlsList="nodownload"
            preload="metadata"
            aria-label="Fender product tour: guided walkthrough of Language, Job Cards, Appointments, Estimates, Gate Pass and SparesHub Enquiries"
            data-testid="product-tour-video"
          >
            Your browser can&apos;t play this video. <a href="/signup" className="underline">Sign up to explore the app instead.</a>
          </video>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
        <SignUpCTA size="lg" testId="tour-sign-up" />
        <Link
          to="/login"
          className="h-[52px] px-6 rounded-lg border border-[#1d3557]/20 text-[14px] font-semibold text-[#1d3557] hover:bg-[#1d3557]/5 inline-flex items-center gap-2"
          data-testid="tour-login-link"
        >
          Already a customer? Log in
        </Link>
      </div>
    </div>
  </section>
);

// ---------------- Languages (dedicated section, right after Hero) ---------
const HOMEPAGE_LANGS = [
  { code: "en", native: "English",     latin: "English" },
  { code: "hi", native: "हिंदी",       latin: "Hindi" },
  { code: "mr", native: "मराठी",       latin: "Marathi" },
  { code: "gu", native: "ગુજરાતી",   latin: "Gujarati" },
  { code: "ta", native: "தமிழ்",       latin: "Tamil" },
  { code: "te", native: "తెలుగు",     latin: "Telugu" },
  { code: "kn", native: "ಕನ್ನಡ",       latin: "Kannada" },
  { code: "ml", native: "മലയാളം",     latin: "Malayalam" },
];

const LanguagesSection = () => (
  <section id="languages" className="bg-[#f1faee]" data-testid="languages-section">
    <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-20">
      <div className="max-w-2xl">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white text-[10px] uppercase tracking-[0.16em] font-bold text-[#457b9d] mb-4 border border-[#a8dadc]/60">
          <Globe2 size={12} /> 8 Languages
        </div>
        <h2 className="text-[26px] md:text-[34px] font-bold text-[#1d3557] tracking-tight leading-tight">
          Fender Speaks Your Workshop’s Language
        </h2>
        <p className="mt-3 text-[15px] text-[#457b9d] leading-relaxed">
          Every team member can use Fender in the language they are most comfortable with.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        {HOMEPAGE_LANGS.map((l) => (
          <div
            key={l.code}
            className="bg-white rounded-xl p-5 border border-[#e6edf1] hover:border-[#a8dadc] transition-colors text-center"
            data-testid={`homepage-lang-${l.code}`}
          >
            <div className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-none">
              {l.native}
            </div>
            <div className="mt-1.5 text-[11px] uppercase tracking-[0.12em] font-semibold text-[#457b9d]">
              {l.latin}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 text-center text-[15px] md:text-[16px] font-semibold text-[#1d3557]" data-testid="languages-tagline">
        One workshop. One system. Eight languages.
      </div>
    </div>
  </section>
);

// ---------------- SparesHub Customer Benefit (Deep Space Blue) ----------
const CUSTOMER_BENEFIT_POINTS = [
  { Icon: ClipboardList, label: "Complete garage management platform" },
  { Icon: Wrench, label: "Built for Indian workshops" },
  { Icon: ShieldCheck, label: "Backed by SparesHub" },
];

const CustomerBenefitSection = () => (
  <section className="bg-[#1d3557] text-white" data-testid="customer-benefit-section">
    <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-20">
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[10px] uppercase tracking-[0.18em] font-bold text-[#a8dadc] mb-5">
          SparesHub Customer Benefit
        </div>
        <h2 className="text-[26px] md:text-[36px] font-bold leading-[1.15] tracking-tight">
          More Than Spare Parts. A Better Way to Run Your Workshop.
        </h2>
      </div>

      <ul className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        {CUSTOMER_BENEFIT_POINTS.map(({ Icon, label }) => (
          <li
            key={label}
            className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10"
            data-testid={`cb-point-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <span className="w-9 h-9 rounded-md bg-[#a8dadc]/25 grid place-items-center text-[#a8dadc]">
              <Icon size={16} strokeWidth={2} />
            </span>
            <span className="text-[14px] font-semibold text-white">{label}</span>
          </li>
        ))}
      </ul>

      <div className="mt-10">
        <SignUpCTA size="lg" testId="cb-sign-up" />
      </div>
    </div>
  </section>
);

// ---------------- Benefits -------------------------------------------------
const BENEFITS = [
  {
    Icon: ClipboardList,
    title: "Create Job Cards Faster",
    body: "Customer, vehicle and predefined service tasks in one workflow.",
  },
  {
    Icon: Car,
    title: "Track Every Vehicle",
    body: "Know what is being repaired, waiting for parts or ready for delivery.",
  },
  {
    Icon: Users,
    title: "Manage Your Team",
    body: "Assign work to advisors, mechanics, storekeepers and drivers.",
  },
  {
    Icon: HeartHandshake,
    title: "Serve Customers Better",
    body: "Create professional estimates, maintain service history and provide faster updates.",
  },
];

const Benefits = () => (
  <section className="bg-[#f1faee]" data-testid="benefits-section">
    <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-20">
      <h2 className="text-[26px] md:text-[34px] font-bold text-[#1d3557] tracking-tight max-w-2xl">
        Run a Faster, Better Organised Workshop
      </h2>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {BENEFITS.map(({ Icon, title, body }) => (
          <div
            key={title}
            className="bg-white rounded-xl p-6 border border-[#e6edf1] hover:border-[#a8dadc] transition-colors"
            data-testid={`benefit-${title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="w-10 h-10 rounded-lg bg-[#a8dadc]/40 grid place-items-center text-[#457b9d] mb-4">
              <Icon size={20} strokeWidth={2} />
            </div>
            <h3 className="text-[16px] font-bold text-[#1d3557] leading-snug">{title}</h3>
            <p className="mt-2 text-[13px] text-[#457b9d] leading-relaxed">{body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------------- Core Features (6 cards) ---------------------------------
const FEATURES = [
  { Icon: FileText,       t: "Job Cards",                  d: "Create jobs, assign work and track repair progress." },
  { Icon: CalendarClock,  t: "Appointments",               d: "Schedule customer visits using Month, Week and Day views." },
  { Icon: ReceiptText,    t: "Estimates",                  d: "Create professional estimates and convert them directly into job cards." },
  { Icon: Package,        t: "Spare Parts",                d: "Manage purchases, stock, job-card issues and reorder alerts." },
  { Icon: ShieldCheck,    t: "Invoices & Gate Pass",       d: "Invoice completed work, record payment and manage vehicle release." },
  { Icon: History,        t: "Customer & Vehicle Records", d: "Maintain complete customer and vehicle service history." },
];

const Features = () => (
  <section id="features" className="bg-white" data-testid="features-section">
    <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-20">
      <h2 className="text-[26px] md:text-[34px] font-bold text-[#1d3557] tracking-tight max-w-2xl">
        Everything Your Workshop Needs
      </h2>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {FEATURES.map(({ Icon, t, d }) => (
          <div
            key={t}
            className="rounded-xl border border-[#e6edf1] p-6 hover:border-[#a8dadc] transition-colors bg-white"
            data-testid={`feature-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <div className="w-10 h-10 rounded-lg bg-[#f1faee] grid place-items-center text-[#457b9d] mb-4">
              <Icon size={20} strokeWidth={2} />
            </div>
            <h3 className="text-[16px] font-bold text-[#1d3557]">{t}</h3>
            <p className="mt-2 text-[13px] text-[#457b9d] leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------------- SparesHub Enquiry (Deep Space Blue) ---------------------
const SparesHubSection = () => (
  <section className="bg-[#1d3557] text-white" data-testid="spareshub-section">
    <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[11px] uppercase tracking-[0.16em] font-semibold text-[#a8dadc] mb-5">
          Powered by SparesHub
        </div>
        <h2 className="text-[26px] md:text-[36px] font-bold leading-[1.15] tracking-tight">
          Need Spare Parts? Send the Requirement to SparesHub in One Click.
        </h2>
        <p className="mt-4 text-[15px] text-[#a8dadc] leading-relaxed max-w-[520px]">
          Select the parts required for a job and send the complete enquiry directly to SparesHub with vehicle details, quantities and photos.
        </p>

        <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-[520px]">
          {[
            "Vehicle details included",
            "Parts requirements included",
            "Job Card linked",
            "Photos included",
          ].map((l) => (
            <li
              key={l}
              className="flex items-center gap-2 text-[13px] text-white"
              data-testid={`sh-point-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <CheckCircle2 size={16} className="text-[#a8dadc]" strokeWidth={2.5} />
              {l}
            </li>
          ))}
        </ul>
      </div>

      <div className="lg:col-span-6">
        <EnquiryMockup />
      </div>
    </div>
  </section>
);

const EnquiryMockup = () => (
  <div className="rounded-xl bg-white text-[#1d3557] shadow-[0_25px_60px_-20px_rgba(0,0,0,0.5)] border border-white/10 overflow-hidden">
    <div className="px-4 py-3 border-b border-[#e6edf1] flex items-center justify-between">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-[#457b9d] font-semibold">
          Job Card
        </div>
        <div className="text-[15px] font-bold">JC-1042 · Maruti Swift</div>
      </div>
      <div className="text-[10px] px-2 py-1 rounded-md bg-[#f1faee] text-[#457b9d] font-medium">
        KA-05-AB-1234
      </div>
    </div>
    <div className="p-4">
      <div className="text-[11px] uppercase tracking-wider text-[#457b9d] font-semibold mb-2">
        Parts Required
      </div>
      <div className="space-y-2">
        {[
          { p: "Brake Pad Set — Front", q: "1 set" },
          { p: "Oil Filter", q: "1 no" },
          { p: "Engine Oil 5W-30", q: "3.5 L" },
        ].map((row) => (
          <div
            key={row.p}
            className="flex items-center justify-between border border-[#e6edf1] rounded-md px-3 py-2"
          >
            <div className="flex items-center gap-2">
              <Package size={14} className="text-[#457b9d]" />
              <span className="text-[13px] font-medium">{row.p}</span>
            </div>
            <span className="text-[12px] text-[#457b9d]">{row.q}</span>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-2 text-[11px] text-[#457b9d]">
        <Camera size={13} /> 3 photos attached
      </div>

      <div className="mt-4 h-11 rounded-lg bg-[#e63946] text-white grid place-items-center font-semibold text-[13px]">
        Submit Enquiry to SparesHub
      </div>
    </div>
  </div>
);

// ---------------- Workflow (6 steps: appointment → delivery) --------------
const WORKFLOW = [
  { Icon: CalendarClock, t: "Book / Receive Vehicle",           d: "Schedule the visit or receive the vehicle." },
  { Icon: Search,        t: "Inspect Vehicle",                   d: "Capture vehicle condition and customer requirements." },
  { Icon: ClipboardList, t: "Create Job Card",                   d: "Record work and assign the team." },
  { Icon: Wrench,        t: "Estimate & Repair",                 d: "Create the estimate and complete approved work." },
  { Icon: Package,       t: "Arrange Parts",                     d: "Issue stock or send the requirement to SparesHub." },
  { Icon: ReceiptText,   t: "Invoice, Payment & Gate Pass",      d: "Complete billing, collect payment and deliver the vehicle." },
];

const Workflow = () => (
  <section id="how-it-works" className="bg-white" data-testid="workflow-section">
    <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-20">
      <h2 className="text-[26px] md:text-[34px] font-bold text-[#1d3557] tracking-tight max-w-2xl">
        From Appointment to Vehicle Delivery
      </h2>

      <div className="relative mt-12">
        {/* connecting line for desktop */}
        <div className="hidden lg:block absolute top-[26px] left-[calc(8.333%+20px)] right-[calc(8.333%+20px)] h-px bg-[#a8dadc]" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 lg:gap-4 relative">
          {WORKFLOW.map(({ Icon, t, d }, i) => (
            <div
              key={t}
              className="flex flex-col items-center text-center"
              data-testid={`workflow-step-${i + 1}`}
            >
              <div className="relative">
                <div className="w-[52px] h-[52px] rounded-full bg-white border border-[#a8dadc] grid place-items-center text-[#457b9d]">
                  <Icon size={22} strokeWidth={2} />
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#1d3557] text-white text-[11px] font-bold grid place-items-center">
                  {i + 1}
                </div>
              </div>
              <div className="mt-4 text-[14px] font-bold text-[#1d3557] leading-tight">
                {t}
              </div>
              <div className="mt-1 text-[12px] text-[#457b9d] max-w-[160px] leading-snug">
                {d}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ---------------- Offline Reliability (standalone section) ---------------
const OFFLINE_POINTS = [
  { Icon: Save,       t: "Automatic saving" },
  { Icon: Zap,        t: "Continue during outages" },
  { Icon: RefreshCw,  t: "Background synchronisation" },
  { Icon: ImageOff,   t: "No blank screens" },
];

const OfflineSection = () => (
  <section className="bg-[#f1faee]" data-testid="offline-section">
    <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
      <div className="md:col-span-5">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white text-[10px] uppercase tracking-[0.16em] font-bold text-[#457b9d] mb-4 border border-[#a8dadc]/60">
          <Wifi size={12} /> Offline Reliability
        </div>
        <h2 className="text-[26px] md:text-[34px] font-bold text-[#1d3557] tracking-tight leading-tight">
          Built for Unreliable Internet
        </h2>
        <p className="mt-4 text-[15px] text-[#457b9d] leading-relaxed max-w-[440px]">
          Fender saves work locally and automatically syncs when connectivity returns.
        </p>
      </div>
      <ul className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {OFFLINE_POINTS.map(({ Icon, t }) => (
          <li
            key={t}
            className="flex items-center gap-3 p-4 rounded-xl bg-white border border-[#e6edf1]"
            data-testid={`offline-${t.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span className="w-9 h-9 rounded-md bg-[#f1faee] grid place-items-center text-[#457b9d]">
              <Icon size={16} strokeWidth={2} />
            </span>
            <span className="text-[14px] text-[#1d3557] font-semibold">{t}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

// ---------------- Mobile App ---------------------------------------------
const MobileAppSection = () => (
  <section id="mobile-app" className="bg-white" data-testid="mobile-app-section">
    <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-6 order-2 lg:order-1">
        <PhoneRow />
      </div>
      <div className="lg:col-span-6 order-1 lg:order-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f1faee] text-[11px] uppercase tracking-[0.16em] font-semibold text-[#457b9d] mb-4">
          <Smartphone size={12} /> Mobile Ready
        </div>
        <h2 className="text-[26px] md:text-[36px] font-bold text-[#1d3557] leading-[1.1] tracking-tight">
          Your Workshop in Your Pocket
        </h2>
        <p className="mt-4 text-[15px] text-[#457b9d] leading-relaxed max-w-[520px]">
          Create job cards, inspect vehicles, update repairs, manage parts and handle vehicle delivery from mobile.
        </p>
        <div className="mt-7">
          <SignUpCTA size="lg" testId="mobile-app-sign-up" />
        </div>
        <p className="mt-3 text-[13px] text-[#457b9d]" data-testid="mobile-availability-line">
          Available on Web, Android and iOS.
        </p>
      </div>
    </div>
  </section>
);

const PhoneRow = () => (
  <div className="flex justify-center gap-6" data-testid="phone-mockups">
    {[
      { title: "Job Cards", accent: "#e63946" },
      { title: "Inspection", accent: "#457b9d" },
    ].map((p, idx) => (
      <div
        key={p.title}
        className="w-[160px] rounded-[26px] bg-[#1d3557] p-1.5 shadow-[0_25px_60px_-25px_rgba(29,53,87,0.6)]"
        style={{ transform: idx === 0 ? "rotate(-3deg)" : "rotate(3deg)" }}
      >
        <div className="rounded-[20px] bg-white overflow-hidden">
          <div className="h-5 bg-[#1d3557] flex items-center justify-center">
            <div className="w-14 h-1.5 rounded-full bg-black/40" />
          </div>
          <div className="p-3">
            <div className="text-[10px] font-bold text-[#1d3557]">{p.title}</div>
            <div className="text-[8px] text-[#457b9d] mb-2">Today</div>
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="rounded-md border border-[#e6edf1] p-2 mb-1.5"
              >
                <div className="flex items-center gap-1.5">
                  <div
                    className="w-1 h-6 rounded-full"
                    style={{ background: p.accent }}
                  />
                  <div className="flex-1">
                    <div className="text-[9px] font-semibold text-[#1d3557]">
                      Task {i}
                    </div>
                    <div className="text-[7px] text-[#457b9d]">KA-0{i} · Swift</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ---------------- Testimonials --------------------------------------------
// Real workshop customers. Names, garages and locations approved by the
// respective owners for public display on FenderApp.com.
const TESTIMONIALS = [
  {
    quote:
      "Fender has made our job-card process much more organised. Our team can quickly see the vehicle, work required and current status without depending on registers or scattered messages.",
    author: "Rohit Shetty",
    garage: "Shetty Motors, Navi Mumbai",
  },
  {
    quote:
      "We wanted software that our team would actually use every day. Fender is simple for our mechanics while still giving us complete visibility of workshop operations.",
    author: "Tukaram Aghao",
    garage: "Sai Motor Garages, Kolhapur",
  },
  {
    quote:
      "The spare-parts enquiry from the job card saves us a lot of effort. We can identify the requirement and send it directly to SparesHub without preparing the enquiry again.",
    author: "B.S. Venugopal",
    garage: "New Mangalore Multicars, Belagavi",
  },
];

const TestimonialsSection = () => (
  <section className="bg-white" data-testid="testimonials-section">
    <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-20">
      <div className="max-w-2xl">
        <h2 className="text-[26px] md:text-[34px] font-bold text-[#1d3557] tracking-tight leading-tight">
          Trusted by Workshop Owners
        </h2>
        <p className="mt-3 text-[15px] text-[#457b9d] leading-relaxed">
          Built around the way independent and multi-brand workshops actually work.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
        {TESTIMONIALS.map((t, i) => (
          <div
            key={i}
            data-testid={`testimonial-${i + 1}`}
            data-placeholder="true"
            className="bg-white rounded-xl p-6 border border-[#e6edf1] flex flex-col"
          >
            <Quote size={22} className="text-[#e63946] mb-4" strokeWidth={2} />
            <p className="text-[14px] text-[#1d3557] leading-relaxed flex-1">
              “{t.quote}”
            </p>
            <div className="mt-5 pt-4 border-t border-[#f1faee]">
              <div className="text-[13px] font-bold text-[#1d3557]">{t.author}</div>
              <div className="text-[12px] text-[#457b9d]">{t.garage}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------------- Final CTA -----------------------------------------------
const FinalCTA = () => (
  <section className="bg-[#1d3557]" data-testid="final-cta">
    <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-20 text-center">
      <h2 className="text-[28px] md:text-[38px] font-bold text-white tracking-tight max-w-2xl mx-auto leading-tight">
        Run Your Workshop Better with Fender
      </h2>
      <div className="mt-8 flex justify-center">
        <SignUpCTA size="lg" testId="final-cta-sign-up" />
      </div>
      <div className="mt-4 text-[13px] text-[#a8dadc]">
        Already use Fender?{" "}
        <Link to="/login" className="underline hover:text-white font-semibold" data-testid="final-cta-login">
          Login
        </Link>
      </div>
    </div>
  </section>
);

// ---------------- Footer ---------------------------------------------------
const Footer = () => (
  <footer id="contact" className="bg-white border-t border-[#e6edf1]" data-testid="footer">
    <div className="max-w-[1200px] mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
      <div className="col-span-2 md:col-span-1">
        <img
          src="/fender-logo-lockup.png"
          alt="Fender by SparesHub"
          className="h-10 w-auto mb-4"
          width="720"
          height="208"
        />
        <p className="text-[13px] text-[#457b9d] leading-relaxed max-w-[240px]">
          Garage management software for multi-brand workshops and mechanics.
        </p>
      </div>

      <FooterCol
        title="Product"
        links={[
          { label: "Features", href: "#features" },
          { label: "How It Works", href: "#how-it-works" },
          { label: "Mobile App", href: "#mobile-app" },
          { label: "Languages", href: "#languages" },
        ]}
      />
      <FooterCol
        title="Company"
        links={[
          { label: "About Fender", href: "/about", route: true, testId: "footer-link-about" },
          { label: "SparesHub", href: "https://spareshub.com", ext: true },
          { label: "Contact", href: "mailto:contact@fenderapp.com", icon: Mail },
          { label: "Support", href: "mailto:contact@fenderapp.com", icon: Phone },
          { label: "Login", href: "/login", route: true },
        ]}
      />
      <FooterCol
        title="Legal"
        links={[
          { label: "Privacy Policy", href: "/privacy", route: true, testId: "footer-link-privacy" },
          { label: "Terms of Use", href: "/terms", route: true, testId: "footer-link-terms" },
          { label: "Legal & Data Policy", href: "/legal", route: true, testId: "footer-link-legal" },
          { label: "Account Deletion", href: "/account-deletion", route: true, testId: "footer-link-account-deletion" },
        ]}
      />
    </div>
    <div className="border-t border-[#f1faee]">
      <div
        className="max-w-[1200px] mx-auto px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-1.5 text-[12px] text-[#457b9d]"
        data-testid="footer-legal"
      >
        <div>
          © {COPYRIGHT_YEAR} {LEGAL_NAME}. All rights reserved.
        </div>
        <div className="font-semibold" data-testid="footer-brand-endorsement">
          Fender by SparesHub
        </div>
      </div>
    </div>
  </footer>
);

const FooterCol = ({ title, links }) => (
  <div>
    <div className="text-[12px] font-bold text-[#1d3557] uppercase tracking-[0.12em] mb-3">
      {title}
    </div>
    <ul className="space-y-2">
      {links.map((l) =>
        l.route ? (
          <li key={l.label}>
            <Link
              to={l.href}
              data-testid={l.testId}
              className="text-[13px] text-[#457b9d] hover:text-[#e63946] transition-colors"
            >
              {l.label}
            </Link>
          </li>
        ) : (
          <li key={l.label}>
            <a
              href={l.href}
              target={l.ext ? "_blank" : undefined}
              rel={l.ext ? "noopener noreferrer" : undefined}
              data-testid={l.testId}
              className="text-[13px] text-[#457b9d] hover:text-[#e63946] transition-colors"
            >
              {l.label}
            </a>
          </li>
        )
      )}
    </ul>
  </div>
);

// ---------------- Page ----------------------------------------------------
export default function Landing() {
  return (
    <div
      className="min-h-screen bg-white text-[#1d3557]"
      style={{
        fontFamily:
          '"Noto Sans","Noto Sans Devanagari","Noto Sans Gujarati","Noto Sans Kannada","Noto Sans Tamil","Noto Sans Telugu","Noto Sans Malayalam",Arial,sans-serif',
      }}
      data-testid="landing-page"
    >
      <Header />
      <main>
        <Hero />
        <ProductTour />
        <LanguagesSection />
        <CustomerBenefitSection />
        <Benefits />
        <Features />
        <SparesHubSection />
        <Workflow />
        <OfflineSection />
        <MobileAppSection />
        <TestimonialsSection />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
